import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useConfirmXeroImport, useWithdrawXeroImport } from '../services/conversionsApi';
import { activeXeroConfirmation } from '../lib/xeroImport';
import { formatLocalDateTime } from '../lib/provenance';
import type { ManualJournal } from '../types';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  row: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
});

/**
 * B2 (docs/core-workflow.md): the step after "exported". An export produces a file; it cannot know the journal reached
 * Xero. So an exported journal reads "not yet confirmed in Xero", with the reference to search for, until a bookkeeper
 * confirms it. A mistaken confirmation is withdrawn (kept, never deleted). Nothing here ever says "in Xero" on its own.
 */
export default function XeroImportPanel({
  journal,
  onChange,
}: {
  journal: ManualJournal;
  onChange: (updated: ManualJournal) => void;
}) {
  const styles = useStyles();
  const { t, i18n } = useTranslation();
  const [number, setNumber] = useState('');
  const confirm = useConfirmXeroImport();
  const withdraw = useWithdrawXeroImport();

  const active = activeXeroConfirmation(journal);
  // Only a reference the exported FILE carries: a journal exported before references existed has none to search for.
  const reference = journal.xeroReference ?? null;
  const failure = confirm.error ?? withdraw.error;

  return (
    <div className={styles.panel}>
      {failure && (
        <MessageBar intent="error">
          <MessageBarBody>{failure instanceof Error ? failure.message : t('common.unknownError')}</MessageBarBody>
        </MessageBar>
      )}

      {active ? (
        <MessageBar intent="success">
          <MessageBarBody>
            {t('journalPage.xero.confirmed', {
              name: active.confirmedByName ?? t('uploads.removed.someone'),
              date: formatLocalDateTime(active.confirmedAt, i18n.language),
            })}
            {active.xeroJournalNumber ? ` ${t('journalPage.xero.number', { number: active.xeroJournalNumber })}` : ''}{' '}
            <Button
              size="small"
              appearance="transparent"
              disabled={withdraw.isPending}
              onClick={() =>
                withdraw.mutate({ clientId: journal.clientId, journalId: journal.id }, { onSuccess: onChange })
              }
            >
              {t('journalPage.xero.withdraw')}
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : (
        <>
          <MessageBar intent="warning">
            <MessageBarBody>
              <strong>{t('journalPage.xero.unconfirmedTitle')}</strong>{' '}
              {reference
                ? t('journalPage.xero.unconfirmedBody', { reference })
                : t('journalPage.xero.unconfirmedBodyNoRef')}
            </MessageBarBody>
          </MessageBar>
          <div className={styles.row}>
            <Field label={t('journalPage.xero.numberLabel')} hint={t('journalPage.xero.numberHint')}>
              <Input value={number} onChange={(_e, data) => setNumber(data.value)} />
            </Field>
            <Button
              appearance="primary"
              disabled={confirm.isPending}
              icon={confirm.isPending ? <Spinner size="tiny" /> : undefined}
              onClick={() =>
                confirm.mutate(
                  { clientId: journal.clientId, journalId: journal.id, xeroJournalNumber: number.trim() || null },
                  { onSuccess: onChange },
                )
              }
            >
              {t('journalPage.xero.confirm')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
